/**
 * Utils for URL.
 */

/**
 * Builds query string with '?' if needed. Omits empty/undefined params.
 * See: https://stackoverflow.com/questions/62989310/how-to-remove-empty-query-params-using-urlsearchparams
 */
export function getQueryString(params: Record<string, string | number | undefined>) {
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    // todo: make more robust solution handling arrays
    if (value !== undefined && value !== '') searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

/**
 * NexReturns single string value.
 * Useful for getting values from nextjs router query.
 * See: https://www.reddit.com/r/nextjs/comments/nb4jju/check_if_routerqueryparam_is_string_or_string/
 */
export function getStringValue(value?: string | string[]) {
  const singleValue = Array.isArray(value) ? value[0] : value;
  return singleValue || '';
}

// there are two possible definitions of absolute URI
// the strict one is that "it starts with a protocol"
// but in our case we need the looser one - a protocol-relative URI (for instance "//www.domain.com/a.html") should be treated as absolute
// because it contains a domain name, it leads out of a React app, and hence needs the same kind of navigation in React as any fully-absolute URI
export function isUriAbsolute(uri: string) {
  return /^[a-z][a-z\d+\-.]*:/i.test(uri) || uri.startsWith('//');
}

/**
 * Ensures a URL is absolute by prefixing "https://" when no scheme is present.
 *
 * Sanity-authored links are expected to be absolute external URLs, but the protocol is sometimes
 * omitted (e.g. "google.com"). Without a scheme, Next.js would resolve them relative to the current
 * route (e.g. "/status/po/google.com"), so we prepend "https://" to keep them external.
 * Already-absolute URLs (with a protocol or protocol-relative "//...") are returned unchanged.
 */
export function toAbsoluteUrl(url: string) {
  return isUriAbsolute(url) ? url : `https://${url}`;
}

/**
 * Safely joins URL path segments using slash normalization.
 *
 * This helper exists because:
 * - Simple string concatenation (`base + "/" + path`) is error-prone and often produces double slashes or missing separators.
 * - The `URL` constructor cannot be used when the base is a relative path (e.g. "/api") or when the origin is intentionally unknown.
 * - fetch() accepts relative URLs, but `new URL()` requires an absolute base with a protocol and host.
 *
 * The function guarantees:
 * - exactly one "/" between path segments
 * - compatibility with relative URLs in both browser and Node.js
 *
 * @param {string} baseUrl - Base path or URL (absolute or relative).
 * @param {string} path - Path to append (may contain a query or hash part)
 * @returns {string} A normalized URL
 */
export function joinUrl(baseUrl: string, path: string) {
  if (!baseUrl.endsWith('/')) baseUrl = baseUrl + '/';
  return baseUrl + path.replace(/^\/+/g, '');
}
